import { compose, over, set, lensProp, path, pick, mergeAll } from 'ramda';
import { formatPhone, formatPostalCode, formatDocument } from '../../../helpers/formatters';
import { convertToEmptyString } from '../../../helpers/converters';

import {
  SET_ESTABLISHMENT_ID,
  SET_ESTABLISHMENT_UUID,
  SET_PREVIOUS_ESTABLISHMENT_ID,
  SET_BUSINESS_INFO,
  SET_ESTABLISHMENT_TYPES,
  CLEAR_BUSINESS_INFO,
  CLEAR_ADDRESS,
  HANDLE_POSTAL_CODE,
  HANDLE_LOADING,
  HANDLE_EDITING,
  SET_ESTABLISHMENTS,
  SET_ESTABLISHMENT_ERRORS,
  CLEAR_ESTABLISHMENT_ERRORS,
  SET_SUPERLOGICA_LIST,
  SET_JUMP_CAR_STORE_DOWNLOAD,
  SET_IS_JUMP_CAR_STORE_DOWNLOAD_LOADING,
  SET_IS_JUMP_CAR_STORE_DOWNLOAD_AVAILABLE
} from './constants';

const defaultState = {
  establishmentId: '',
  establishmentUuid: '',
  businessId: '',
  previousEstablishmentId: '',
  jumpCarStoreDownload: {},
  isJumpCarStoreDownloadLoading: false,
  isJumpCarStoreDownloadAvailable: false,
  accessKey: null,
  isLoading: false,
  isPostalCodeLoading: false,
  establishmentTypes: [],
  establishments: [],
  errors: {
    establishmentTypes: null,
    establishmentName: null,
    street: null,
    number: null,
    complement: null,
    district: null,
    city: null,
    state: null,
    vacanciesMarks: null
  },
  submit: {
    userId: null,
    boostPackage: 0,
    debitWarning: 0,
    premiumPackage: 0,
    appPermission: 0,
    establishmentTypes: [],
    establishmentName: '',
    companyName: '',
    documentId: '',
    phone: '',
    email: '',
    vacanciesMarks: '',
    postalCode: '',
    street: '',
    number: '',
    complement: '',
    district: '',
    city: '',
    state: '',
    businessEstablishmentId: ''
  },
  superlogicaList: [],
  isEditing: false
}

export default (state = defaultState, action) => {
  switch (action.type) {
    case SET_ESTABLISHMENT_ID: {
      return {
        ...state,
        establishmentId: action?.id
      }
    }
    case SET_ESTABLISHMENT_UUID: {
      return {
        ...state,
        establishmentUuid: action?.uuid
      }
    }
    case SET_PREVIOUS_ESTABLISHMENT_ID: {
      return {
        ...state,
        previousEstablishmentId: action?.establishmentId
      }
    }
    case SET_ESTABLISHMENTS: {
      return set(lensProp('establishments'), action.data, state);
    }
    case SET_BUSINESS_INFO: {
      const setAddress = compose(
        over(lensProp('postalCode'), formatPostalCode),
        path(['addresses', 0])
      );

      const establishmentTypesStringArray = action.data.establishmentTypes;

      const establishmentTypes = {
        establishmentTypes: establishmentTypesStringArray.map(Number)
      }

      const setBusinessInfo = compose(
        over(lensProp('vacanciesMarks'), convertToEmptyString),
        over(lensProp('vacanciesMarks'), convertToEmptyString),
        over(lensProp('documentId'), formatDocument),
        over(lensProp('phone'), formatPhone),
        pick([
          'userId',
          'establishmentName',
          'companyName',
          'documentId',
          'phone',
          'email',
          'vacanciesMarks',
          'boostPackage',
          'debitWarning',
          'premiumPackage',
          'appPermission'
        ])
      );

      const businessInfo = compose(
        set(lensProp('isEditing'), false),
        set(lensProp('accessKey'), action?.data?.accessKey),
        set(lensProp('address'), setAddress(action?.data)),
        set(lensProp('businessId'), action?.data?.businessId),
        set(
          lensProp('submit'),
          mergeAll([
            setBusinessInfo(action.data),
            setAddress(action.data),
            establishmentTypes
          ])
        )
      );

      const result = businessInfo(state);

      return result;
    }
    case SET_ESTABLISHMENT_ERRORS: {
      return {
        ...state,
        errors: {
          establishmentTypes: action.errors?.establishmentTypes ? action.errors?.establishmentTypes[0] : null,
          establishmentName: action.errors?.establishmentName ? action.errors?.establishmentName[0] : null,
          street: action.errors?.street ? action.errors?.street[0] : null,
          number: action.errors?.number ? action.errors?.number[0] : null,
          complement: action.errors?.complement ? action.errors?.complement[0] : null,
          district: action.errors?.district ? action.errors?.district[0] : null,
          city: action.errors?.city ? action.errors?.city[0] : null,
          state: action.errors?.state ? action.errors?.state[0] : null,
          vacanciesMarks: action.errors?.vacanciesMarks ? action.errors?.vacanciesMarks[0] : null
        }
      }
    }
    case CLEAR_ESTABLISHMENT_ERRORS: {
      return {
        ...state,
        errors: defaultState.errors
      }
    }
    case SET_ESTABLISHMENT_TYPES: {
      return {
        ...state,
        establishmentTypes: action?.establishmentTypes?.map(type => +type)
      }
    }
    case CLEAR_BUSINESS_INFO: {
      return {
        ...state,
        submit: defaultState.submit
      }
    }
    case CLEAR_ADDRESS: {
      return {
        ...state,
        submit: {
          ...state.submit,
          postalCode: '',
          street: '',
          number: 0,
          complement: '',
          district: '',
          city: '',
          state: ''
        },
        isPostalCodeLoading: false
      }
    }
    case SET_SUPERLOGICA_LIST: {
      if(action?.data?.length == 0) {
        return {
          ...state,
          superlogicaList: []
        }
      }

      return {
        ...state,
        superlogicaList: action.data?.map(superlogica => {
          return {
            label: superlogica?.name,
            value: superlogica?.superlogicaId
          }
        })
      }
    }
    case SET_JUMP_CAR_STORE_DOWNLOAD: {
      return {
        ...state,
        jumpCarStoreDownload: action.data
      }
    }
    case SET_IS_JUMP_CAR_STORE_DOWNLOAD_LOADING: {
      return {
        ...state,
        isJumpCarStoreDownloadLoading: action.isLoading
      }
    }
    case SET_IS_JUMP_CAR_STORE_DOWNLOAD_AVAILABLE: {
      return {
        ...state,
        isJumpCarStoreDownloadAvailable: action.isAvailable
      }
    }
    case HANDLE_POSTAL_CODE: {
      return set(lensProp('isPostalCodeLoading'), action.status, state);
    }
    case HANDLE_LOADING: {
      return set(lensProp('isLoading'), action.status, state);
    }
    case HANDLE_EDITING: {
      return set(lensProp('isEditing'), action.status, state);
    }
    default: {
      return state;
    }
  }
}